import { Component } from "react"

/* Modal */
import Modal from "react-modal"

/* REST API */
import { createCurrency as create, editCurrency as edit } from "../../api/Table"

/* Components */
import { Input } from "../../components"
import { pushToast } from "../../components/Toast"

/* Icons */
import { CurrencyIcon, ChipIcon, CloseIcon, CheckIcon } from "../../components/Icons"
import { LoadingOutlined } from "@ant-design/icons"

/* Redux */
import { connect } from "react-redux"

/* Translate */
import { t } from "../../translates/translate"

/* Helpers */
import { utils } from "../../helpers"


/* EMPTY FORM — EVERY LIMIT IS KEPT AS A STRING, THE COLUMNS BEHIND THEM ARE TEXT */
const EMPTY = {
    id: 0,
    currencyID: 0,
    name: "",
    code: "",
    symbol: "",
    max: "",
    min: "",
    maxPay: "",
    maxBonus: "",
    minBonus: "",
    chips: "",
    maxPerfectPair: "",
    maxTwentyOne: "",
    maxBust: "",
    maxCrazySeven: "",
    applyToAll: false,
}


/* Widget CurrencyForm — THE LIMITS OF ONE CURRENCY, THE PANEL OPENS IT THROUGH A REF */
class CurrencyForm extends Component {

    constructor() {
        super()

        this.state = {
            visible: false,
            type: "create",
            waiting: false,
            error: "",

            ...EMPTY,
        }
    }

    /* Open Action — an edit carries the row of the list, a creation starts empty */
    open = ({ type = "create", item = null }) => {

        /* Create */
        if (type !== "edit" || !item) {
            this.setState({ visible: true, type: "create", waiting: false, error: "", ...EMPTY })
            return
        }

        /* Edit */
        this.setState({
            visible: true,
            type: "edit",
            waiting: false,
            error: "",
            id: item.id,
            currencyID: item.currencyID,
            name: utils.checkValue(item.name),
            code: utils.checkValue(item.code),
            symbol: utils.checkValue(item.symbol),
            max: utils.checkValue(item.max),
            min: utils.checkValue(item.min),
            maxPay: utils.checkValue(item.maxPay),
            maxBonus: utils.checkValue(item.maxBonus),
            minBonus: utils.checkValue(item.minBonus),
            chips: utils.checkValue(item.chips),
            maxPerfectPair: utils.checkValue(item.maxPerfectPair),
            maxTwentyOne: utils.checkValue(item.maxTwentyOne),
            maxBust: utils.checkValue(item.maxBust),
            maxCrazySeven: utils.checkValue(item.maxCrazySeven),
            applyToAll: false,
        })
    }

    /* Close Action */
    close = () => {

        /* Fields */
        const { waiting } = this.state

        /* A Running Request Is Never Interrupted */
        if (waiting) {
            return
        }

        this.setState({ visible: false, type: "create", error: "", ...EMPTY })
    }

    /* Read The Error Message Of A Response */
    message = (response) => {

        if (response && response.status === 201) {
            return t("currency.alreadyAdded")
        }

        if (response && (response.status === 401 || response.status === 403)) {
            return t("swal.noAccess")
        }

        if (response && response.status === 498) {
            return t("swal.invalidKey")
        }

        return t("swal.generic")
    }

    /* Pick A Currency In The Create Form */
    pick = (item) => {

        this.setState({
            error: "",
            currencyID: item.id,
            name: utils.checkValue(item.name),
            code: utils.checkValue(item.code),
            symbol: utils.checkValue(item.symbol),
        })
    }

    /* CHECK THE FORM — THE VERY SAME FIELDS THE SERVER DEMANDS */
    check = () => {

        /* Fields */
        const { blackjack } = this.props
        const { type, currencyID, max, min, maxPay, chips, maxPerfectPair, maxTwentyOne, maxBust, maxCrazySeven } = this.state

        /* Check Currency */
        if (type === "create" && !parseInt(currencyID)) {
            this.setState({ error: t("currency.enterCurrency") })
            return false
        }

        /* Check Maximum Bet — the server refuses a zero as well */
        if (`${max}`.trim() === "" || !(parseFloat(max) > 0)) {
            this._max.error(t("currency.enterMaxBet"))
            return false
        }

        /* Check Minimum Bet */
        if (`${min}`.trim() === "" || !(parseFloat(min) > 0)) {
            this._min.error(t("currency.enterMinBet"))
            return false
        }

        /* Check Chips */
        if (`${chips}`.trim() === "") {
            this._chips.error(t("currency.enterChips"))
            return false
        }

        /* Check Maximum Payout — a blackjack pays by its own table */
        if (!blackjack && `${maxPay}`.trim() === "") {
            this._maxPay.error(t("currency.enterMaxPay"))
            return false
        }

        /* Check Side Bets Of A Blackjack */
        if (blackjack) {

            if (`${maxPerfectPair}`.trim() === "") {
                this._maxPerfectPair.error(t("currency.enterMaxPP"))
                return false
            }

            if (`${maxTwentyOne}`.trim() === "") {
                this._maxTwentyOne.error(t("currency.enterMax21"))
                return false
            }

            if (`${maxBust}`.trim() === "") {
                this._maxBust.error(t("currency.enterMaxBust"))
                return false
            }

            if (`${maxCrazySeven}`.trim() === "") {
                this._maxCrazySeven.error(t("currency.enterMaxCS"))
                return false
            }
        }

        return true
    }

    /* Save Action — one form serves both the creation and the editing */
    save = () => {

        /* Fields */
        const { uid, reload } = this.props
        const { type, id, currencyID, max, min, maxPay, maxBonus, minBonus, chips, maxPerfectPair, maxTwentyOne, maxBust, maxCrazySeven, applyToAll } = this.state

        /* Check Params */
        if (!uid) {
            pushToast({ text: t("widget.unknownError"), type: "error" })
            return
        }

        /* Check Form */
        if (!this.check()) {
            return
        }

        /* Prepare Request */
        const fields = {
            max: `${max}`.trim(),
            min: `${min}`.trim(),
            maxPay: `${maxPay}`.trim(),
            maxBonus: `${maxBonus}`.trim(),
            minBonus: `${minBonus}`.trim(),
            chips: `${chips}`.trim(),
            maxPerfectPair: `${maxPerfectPair}`.trim(),
            maxTwentyOne: `${maxTwentyOne}`.trim(),
            maxBust: `${maxBust}`.trim(),
            maxCrazySeven: `${maxCrazySeven}`.trim(),
            applyToAll: applyToAll ? 1 : 0,
        }

        /* Start Waiting */
        this.setState({ waiting: true })

        /* START REST API */
        const request = type === "edit" ? edit({ uid, id, ...fields }) : create({ uid, currencyID, ...fields })

        request.then((response) => {

            /* Saved */
            if (response.status === 200) {
                pushToast({ text: type === "edit" ? t("currency.savedSuccess") : t("currency.addedSuccess"), type: "success" })
                this.setState({ waiting: false }, () => this.close())
                reload()
                return
            }

            /* Anything Else */
            pushToast({ text: this.message(response), type: "error" })
            this.setState({ waiting: false })

        }).catch(() => {
            pushToast({ text: t("swal.generic"), type: "error" })
            this.setState({ waiting: false })
        })

    }

    /* Draw The Currency Of The Form */
    _currency = () => {

        /* Fields */
        const { currencies, added } = this.props
        const { type, currencyID, error, name, code, symbol } = this.state

        /* Edit — the currency of a row never changes */
        if (type === "edit") {

            return (
                <div className="tbl--section">

                    <div className="tbl--section-title">
                        <CurrencyIcon size={14} />
                        {t("currency.title")}
                    </div>

                    <div className="tbl--cur-picked">
                        <div className="tbl--cur-badge">{utils.checkValue(symbol, code)}</div>
                        <div className="tbl--cur-text">
                            <h3>{name}</h3>
                            <p>{code}</p>
                        </div>
                    </div>

                </div>
            )
        }

        return (
            <div className="tbl--section">

                <div className="tbl--section-title">
                    <CurrencyIcon size={14} />
                    {t("currency.selectCurrency")}
                </div>

                <div className="tbl--pick">
                    {currencies.map((item, index) => {

                        /* Fields */
                        const exists = added.some((row) => parseInt(row.currencyID) === parseInt(item.id))
                        const active = parseInt(currencyID) === parseInt(item.id)

                        return (
                            <div
                                onClick={() => exists ? null : this.pick(item)}
                                className={`tbl--pick-item ${exists ? "added" : ""} ${active ? "active" : ""}`}
                                key={`${index}`}
                            >
                                <div className="tbl--pick-symbol">{utils.checkValue(item.symbol, item.code)}</div>
                                <div className="tbl--pick-text">
                                    <h4>{item.code}</h4>
                                    <p>{item.name}</p>
                                </div>
                                {exists && <div className="tbl--pick-mark"><CheckIcon size={11} /></div>}
                            </div>
                        )
                    })}
                </div>

                {/* Error Of The Picker */}
                {error !== "" && <div className="tbl--error">{error}</div>}

            </div>
        )
    }

    /* Draw The Side Bets Of A Blackjack */
    _sideBets = () => {

        /* Fields */
        const { blackjack } = this.props
        const { maxPerfectPair, maxTwentyOne, maxBust, maxCrazySeven } = this.state

        /* Check Game */
        if (!blackjack) {
            return null
        }

        return (
            <div className="tbl--section">

                <div className="tbl--section-title">
                    <CurrencyIcon size={13} />
                    {t("currency.sideBets")}
                </div>

                <div className="tbl--inputs">
                    <Input type="number" ref={(ref) => this._maxPerfectPair = ref} label={t("currency.maxPP")} value={maxPerfectPair} onChange={(event) => this.setState({ maxPerfectPair: event.target.value })} />
                    <Input type="number" ref={(ref) => this._maxTwentyOne = ref} label={t("currency.max21")} value={maxTwentyOne} onChange={(event) => this.setState({ maxTwentyOne: event.target.value })} />
                </div>

                <div className="tbl--inputs">
                    <Input type="number" ref={(ref) => this._maxBust = ref} label={t("currency.maxBust")} value={maxBust} onChange={(event) => this.setState({ maxBust: event.target.value })} />
                    <Input type="number" ref={(ref) => this._maxCrazySeven = ref} label={t("currency.maxCS")} value={maxCrazySeven} onChange={(event) => this.setState({ maxCrazySeven: event.target.value })} />
                </div>

            </div>
        )
    }

    /* Галочка «применить ко всем валютам» — доллар выступает базой, остальные считаются по коэффициенту фишки */
    _applyToAll = () => {

        /* Fields */
        const { code, applyToAll } = this.state

        /* Check Currency */
        if (String(code).toLowerCase() !== "usd") {
            return null
        }

        return (
            <div
                onClick={() => this.setState({ applyToAll: !applyToAll })}
                className={`tbl--apply ${applyToAll ? "active" : ""}`}
            >

                <div className="tbl--apply-box">{applyToAll && <CheckIcon size={11} />}</div>

                <div className="tbl--apply-text">
                    <h4>{t("currency.applyToAll")}</h4>
                    <p>{t("currency.applyToAllHint")}</p>
                </div>

            </div>
        )
    }

    render = () => {

        /* Fields */
        const { visible, type, waiting, name, max, min, maxPay, maxBonus, minBonus, chips } = this.state

        return (
            <Modal
                isOpen={visible}
                onRequestClose={() => this.close()}
                className="tbl--form-modal"
                overlayClassName="tbl--form-overlay"
                closeTimeoutMS={200}
            >

                <div className="tbl--form-header">

                    <div className="tbl--modal-icon green">
                        <CurrencyIcon size={18} />
                    </div>

                    <div className="tbl--modal-head-text">
                        <h2>{type === "edit" ? `${t("currency.editTitle")} — ${name}` : t("currency.addTitle")}</h2>
                        <p>{t("currency.limitsSubtitle")}</p>
                    </div>

                    <div onClick={() => this.close()} className="tbl--modal-close">
                        <CloseIcon size={16} />
                    </div>

                </div>

                <div className="tbl--form-body">

                    {/* Draw Currency */}
                    {this._currency()}

                    <div className="tbl--section">

                        <div className="tbl--section-title">
                            <ChipIcon size={13} />
                            {t("currency.limits")}
                        </div>

                        <div className="tbl--inputs three">
                            <Input type="number" ref={(ref) => this._min = ref} label={t("currency.minBet")} value={min} onChange={(event) => this.setState({ min: event.target.value })} />
                            <Input type="number" ref={(ref) => this._max = ref} label={t("currency.maxBet")} value={max} onChange={(event) => this.setState({ max: event.target.value })} />
                            <Input type="number" ref={(ref) => this._maxPay = ref} label={t("currency.maxPay")} value={maxPay} onChange={(event) => this.setState({ maxPay: event.target.value })} />
                        </div>

                        <div className="tbl--inputs">
                            <Input type="number" ref={(ref) => this._minBonus = ref} label={t("currency.minBonusBet")} value={minBonus} onChange={(event) => this.setState({ minBonus: event.target.value })} />
                            <Input type="number" ref={(ref) => this._maxBonus = ref} label={t("currency.maxBonusBet")} value={maxBonus} onChange={(event) => this.setState({ maxBonus: event.target.value })} />
                        </div>

                        <div className="tbl--inputs full">
                            <Input ref={(ref) => this._chips = ref} label={t("currency.chipsLabel")} value={chips} onChange={(event) => this.setState({ chips: event.target.value })} />
                        </div>

                        <div className="tbl--hint">{t("currency.chipsHint")}</div>

                        {this._applyToAll()}

                    </div>

                    {/* Draw Side Bets */}
                    {this._sideBets()}

                </div>

                <div className="tbl--form-footer">

                    <div onClick={() => this.close()} className={`tbl--button ghost ${waiting ? "disabled" : ""}`}>{t("common.cancel")}</div>

                    {waiting
                        ? <div className="tbl--button primary disabled"><LoadingOutlined /></div>
                        : <div onClick={() => this.save()} className="tbl--button primary">{type === "edit" ? t("common.save") : t("currency.add")}</div>
                    }

                </div>

            </Modal>
        )
    }

}

const mapStateToProps = (state) => ({
    language: state.language,
})

export default connect(mapStateToProps, null, null, { forwardRef: true })(CurrencyForm)
